import {type RequestOptions, RequestResponseType} from '../types/request';

import {executeRequest} from './execute';

export interface RequestOptionsStream extends RequestOptions {
  responseType: RequestResponseType.STREAM;
}

// Generic overload excludes STREAM so callers passing STREAM type-narrow to
// the `Promise<Response>` overload; otherwise `RequestOptions` would accept
// STREAM here too and return the wrong shape (`TData` while runtime is
// `Response`). Exported so consumers (e.g. `serviceFactory`) can type their
// local options bag to be non-stream by construction.
export type RequestOptionsNonStream = Omit<RequestOptions, 'responseType'> & {
  responseType?: Exclude<RequestResponseType, RequestResponseType.STREAM>;
};

/**
 * High-level fetch wrapper that resolves routes, injects auth, traces the
 * call via OpenTelemetry, and returns either parsed response data or
 * (when `responseType: STREAM`) the raw {@link Response} with its body
 * still unread so callers can pipe it (SSE / AI SDK Data Stream / etc).
 *
 * STREAM mode finishes the OTel span at TTFB; mid-stream errors are not
 * captured by the pipeline.
 *
 * Throws {@link RequestError} on non-2xx responses or network failures
 * detected before the first byte.
 */
export async function request(
  url: string,
  options: RequestOptionsStream,
): Promise<Response>;
export async function request<TData = unknown>(
  url: string,
  options?: RequestOptionsNonStream,
): Promise<TData>;
export async function request<TData = unknown>(
  url: string,
  options?: RequestOptions,
): Promise<Response | TData> {
  const {response, parsedData} = await executeRequest({url, options});

  if (options?.responseType === RequestResponseType.STREAM) {
    return response;
  }

  return parsedData as TData;
}
